import React from "react";
import { Link } from "react-router-dom";
import Seo from "./Seo";

const labs = [
  {
    id: "malware-lab",
    title: "Malware Analysis Lab",
    description: "Set up a safe environment for analyzing malware samples using virtualization.",
    level: "Senior",
    time: "3-4 hours",
    prerequisites: ["Virtualization", "Windows VM", "Wireshark"],
    link: "/malware-analysis",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <path d="M12 8v4"/>
        <path d="M12 16h.01"/>
      </svg>
    ),
  },
  {
    id: "network-analyzer",
    title: "Network Traffic Analyzer",
    description: "Build a Python tool to capture and analyze network packets.",
    level: "Medior",
    time: "1-2 hours",
    prerequisites: ["Python", "pcap files"],
    link: "/network-traffic-analyzer-1",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  },
  {
    id: "ping-tool",
    title: "Ping Tool",
    description: "Create a network ping utility in Python for connectivity testing.",
    level: "Junior",
    time: "30-45 min",
    prerequisites: ["Python basics"],
    link: "/ping-tool",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    ),
  },
];

const levelColors = {
  Junior: { bg: "rgba(34, 197, 94, 0.1)", text: "#16a34a", border: "rgba(34, 197, 94, 0.3)" },
  Medior: { bg: "rgba(251, 191, 36, 0.1)", text: "#d97706", border: "rgba(251, 191, 36, 0.3)" },
  Senior: { bg: "rgba(239, 68, 68, 0.1)", text: "#dc2626", border: "rgba(239, 68, 68, 0.3)" },
};

export default function Labs() {
  return (
    <div className="labs-container">
      <Seo
        title="Labs - LinuxSecOps"
        description="Hands-on security labs with difficulty levels, time estimates, and prerequisites."
        path="/labs"
      />

      {/* Hero Section */}
      <section className="labs-hero">
        <div className="labs-hero-content">
          <div className="hero-badge">Labs</div>
          <h1 className="labs-hero-title">
            Hands-on Security
            <span className="labs-hero-accent"> Learning</span>
          </h1>
          <p className="labs-hero-description">
            Practical exercises to build real security skills. Each lab includes
            difficulty level, time estimate, and required prerequisites.
          </p>
        </div>
      </section>

      {/* Labs Grid */}
      <div className="labs-content">
        <div className="labs-grid">
          {labs.map((lab) => (
            <Link key={lab.id} to={lab.link} className="lab-card">
              <div className="lab-card-header">
                <div className="lab-icon">{lab.icon}</div>
                <div className="lab-meta">
                  <span
                    className="lab-level"
                    style={{
                      background: levelColors[lab.level].bg,
                      color: levelColors[lab.level].text,
                      borderColor: levelColors[lab.level].border,
                    }}
                  >
                    {lab.level}
                  </span>
                  <span className="lab-time">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    {lab.time}
                  </span>
                </div>
              </div>
              <h3 className="lab-title">{lab.title}</h3>
              <p className="lab-description">{lab.description}</p>
              <div className="lab-prerequisites">
                <span className="lab-prereq-label">Prerequisites:</span>
                <div className="lab-prereq-tags">
                  {lab.prerequisites.map((prereq) => (
                    <span key={prereq} className="lab-prereq-tag">
                      {prereq}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lab-start">
                Start Lab
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {/* Info Section */}
        <div className="labs-info">
          <div className="labs-info-card">
            <h3>How Labs Work</h3>
            <p>
              Each lab is a self-contained exercise with step-by-step instructions.
              Work through the material at your own pace, and use the provided
              code samples and configurations as a starting point.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
